import { AbstractControl, FormGroup } from '@angular/forms';

import { ApiError } from './ApiError';
import { errorCodeText } from './error-messages';

/** The key a server-side rule is placed under, so a template can tell it from a local rule. */
export const SERVER_ERROR_KEY = 'server';

/**
 * Places what it can of a coded 4xx at the controls it names, and returns what it could not
 * place (#178, decision 2).
 *
 * **Opportunistic rather than enumerated**, which is the decision this implements. The match is
 * the last segment of `field`, case-insensitively, against the form's own control names — no
 * per-endpoint mapping, no list of forms. That is what makes it survive the backend's own
 * caveat: a BFF action that proxies to a domain API reports the *domain command's* property
 * path, so `field` arrives as `SettingModel.Value` where the client sent `value`. Keying on the
 * last segment turns that from an unusable hint into a placed message, and anything that still
 * does not match falls through to the toast rather than being dropped.
 *
 * A code with no dictionary entry is never placed: a red field with no text beside it is worse
 * than a red field with a sentence in the toast, so it is returned as unplaced too.
 */
export function applyServerFieldErrors(
  form: FormGroup | null | undefined,
  errors: ApiError[],
): ApiError[] {
  if (!form || !errors?.length) {
    return errors ?? [];
  }

  const byName = new Map<string, AbstractControl>();
  for (const name of Object.keys(form.controls)) {
    byName.set(name.toLowerCase(), form.controls[name]);
  }

  const unplaced: ApiError[] = [];
  for (const error of errors) {
    const control = error.field ? byName.get(lastSegment(error.field)) : undefined;
    const text = control ? errorCodeText(error) : null;

    if (!control || !text) {
      unplaced.push(error);
      continue;
    }

    // Merged rather than replaced: the control's own rules still hold, and a save that fails
    // server-side must not wipe the local `required` the template is already branching on.
    // markAsTouched, because most templates only show feedback for a touched or dirty control —
    // a message placed on an untouched one would be invisible.
    control.setErrors({ ...(control.errors ?? {}), [SERVER_ERROR_KEY]: text });
    control.markAsTouched();
  }
  return unplaced;
}

/**
 * Clears every server-placed error from a form.
 *
 * Called before each submit: a server rule is a statement about the values as they were sent,
 * so leaving the last answer's messages up would tell the user a field is wrong while they are
 * fixing it. Local validators are re-run rather than dropped, so what remains is the form's own
 * verdict.
 */
export function clearServerFieldErrors(form: FormGroup | null | undefined): void {
  if (!form) {
    return;
  }
  for (const name of Object.keys(form.controls)) {
    const control = form.controls[name];
    if (!control.errors?.[SERVER_ERROR_KEY]) {
      continue;
    }
    const { [SERVER_ERROR_KEY]: _removed, ...rest } = control.errors;
    control.setErrors(Object.keys(rest).length ? rest : null);
    control.updateValueAndValidity({ emitEvent: false });
  }
}

/**
 * Whether `form` may be submitted now — and the one place the previous answer's server
 * messages come down (#233).
 *
 * `clearServerFieldErrors` used to run inside `CallApiService` alone, i.e. *behind* every
 * caller's `if (!form.valid) return EMPTY` guard. A placed server error makes its control
 * invalid, so a field the user never touches again — a dropdown, or the street when they go
 * on to correct the postcode — kept the whole form invalid, and every further click on
 * „Speichern" returned EMPTY with nothing on screen to explain why. Angular drops the error
 * on the next value change of *that* control, which is why it only ever bit the untouched
 * ones. Clearing before the verdict makes a server rule what it is: a statement about the
 * values of the last submit.
 */
export function submittable(form: FormGroup | null | undefined): boolean {
  clearServerFieldErrors(form);
  return !!form?.valid;
}

/** `SettingModel.Value` → `value`; `Salutation` → `salutation`. */
function lastSegment(field: string): string {
  const segments = field.split('.');
  return segments[segments.length - 1].toLowerCase();
}
